import {
  AppstoreOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CrownFilled,
  CrownOutlined,
  EditOutlined,
  ExportOutlined,
  EyeInvisibleOutlined,
  FieldTimeOutlined,
  EyeOutlined,
  HistoryOutlined,
  PlusOutlined,
  ScheduleOutlined,
  StopOutlined,
  SwapOutlined,
  TableOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  UserOutlined,
  UserSwitchOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import type { Membership, Team } from '../../DTOS/ResourcePlanning';
import AllocationModal from '../../components/ResourcePlanning/AllocationModal';
import { MembershipPeriodModal, TeamStartModal } from '../../components/ResourcePlanning/MembershipPeriodModal';
import TeamBoard from '../../components/ResourcePlanning/TeamBoard';
import {
  colorOf,
  DATE,
  DISPLAY_DATE,
  hours,
  IconButton,
  initials,
  isLeaderMember,
  memberState,
  membershipPeriod,
  PlanningLink,
  teamPayload,
  today,
  transferMessage,
  useFeedback,
  useMe,
  UserSelect,
  useTeams,
} from '../../components/ResourcePlanning/shared';
import api from '../../services/resourcePlanning/ResourcePlanningService';

type MemberMode = 'add' | 'move' | 'swap' | 'end';
interface MemberAction {
  mode: MemberMode;
  team?: Team;
  member?: Membership;
}
type Layout = 'board' | 'table';
/** Allocation started from a team: the whole team, or one member when given. */
interface TeamAllocation {
  team: Team;
  member?: Membership;
  from: string;
}
/** Current and scheduled memberships, one per person. */
const activeMembers = (team: Team) => [
  ...new Map(
    team.memberships.filter((member) => memberState(member) !== 'history').map((member) => [member.userId, member]),
  ).values(),
];

const TeamModal: React.FC<{ team: Team | null; onClose: () => void }> = ({ team, onClose }) => {
  const [form] = Form.useForm();
  const feedback = useFeedback();
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([]);
  const memberIds: string[] = Form.useWatch('memberIds', form) || [];
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  const existing = team ? activeMembers(team) : [];
  const leaderOptions = [
    ...existing.map((member) => ({ value: member.userId, label: member.userName })),
    ...(team?.leaderUserId && !existing.some((member) => member.userId === team.leaderUserId)
      ? [{ value: team.leaderUserId, label: `${team.leaderName} (nu mai este membru)` }]
      : []),
    ...picked.filter(
      (option) => memberIds.includes(option.value) && !existing.some((member) => member.userId === option.value),
    ),
  ];
  const save = async () => {
    try {
      const values = await form.validateFields();
      run.mutate(async () => {
        await api.saveTeam({
          id: team?.id,
          name: values.name,
          isActive: values.isActive,
          dailyMinutes: Math.round(values.dailyHours * 60),
          leaderUserId: values.leaderUserId ?? null,
          memberIds: values.memberIds,
          membersFrom: values.membersFrom?.format(DATE),
          expectedVersion: team?.rowVersion || 0,
        });
        feedback.success(team ? 'Echipa actualizata.' : 'Echipa creata.');
        onClose();
      });
    } catch (error) {
      feedback.error(error);
    }
  };
  return (
    <Modal
      open
      title={team ? 'Editeaza echipa' : 'Echipa noua'}
      onCancel={onClose}
      onOk={save}
      confirmLoading={run.isPending}
      okText='Salveaza'
      cancelText='Renunta'
      destroyOnHidden
    >
      <Form
        form={form}
        layout='vertical'
        initialValues={{
          name: team?.name,
          isActive: team?.isActive ?? true,
          dailyHours: (team?.dailyMinutes ?? 480) / 60,
          leaderUserId: team?.leaderUserId ?? undefined,
          membersFrom: dayjs(),
        }}
      >
        <Form.Item name='name' label='Nume echipa' rules={[{ required: true, whitespace: true }]}>
          <Input maxLength={200} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={14}>
            <Form.Item
              name='dailyHours'
              label={
                <span>
                  <ClockCircleOutlined /> Program de lucru
                </span>
              }
              rules={[{ required: true }]}
              extra='Totalul zilnic peste program este marcat ca depasire.'
            >
              <InputNumber
                min={0.5}
                max={24}
                step={0.5}
                precision={2}
                addonAfter='ore / zi'
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item name='isActive' label='Activa' valuePropName='checked'>
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name='memberIds'
          label={
            <span>
              <UserAddOutlined /> {team ? 'Adauga membri' : 'Membri'}
            </span>
          }
          extra='Persoanele care apartin altei echipe sunt mutate de la data selectata; alocarile existente raman neschimbate.'
        >
          <UserSelect multiple onPicked={setPicked} />
        </Form.Item>
        {memberIds.length > 0 && (
          <Form.Item name='membersFrom' label='Membri incepand cu' rules={[{ required: true }]}>
            <DatePicker format={DISPLAY_DATE} />
          </Form.Item>
        )}
        <Form.Item
          name='leaderUserId'
          label={
            <span>
              <CrownFilled style={{ color: '#faad14' }} /> Sef echipa
            </span>
          }
          extra='Seful desemnat primeste automat rolul ERP Sef echipa proiect.'
        >
          <Select
            allowClear
            options={leaderOptions}
            placeholder='Selectati din membrii echipei'
            notFoundContent='Adaugati mai intai membri'
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const MemberModal: React.FC<{ action: MemberAction; teams: Team[]; onClose: () => void }> = ({
  action,
  teams,
  onClose,
}) => {
  const [form] = Form.useForm();
  const feedback = useFeedback();
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  const { mode, team, member } = action;
  const titles: Record<MemberMode, string> = {
    add: `Adauga membri in ${team?.name}`,
    move: 'Muta in alta echipa',
    swap: 'Schimb intre echipe',
    end: `Scoate din ${team?.name}`,
  };
  const swapOptions = teams
    .filter((item) => item.isActive && item.id !== team?.id)
    .flatMap((item) =>
      item.memberships
        .filter((other) => memberState(other) === 'current')
        .map((other) => ({ value: other.userId, label: `${other.userName} · ${item.name}` })),
    );
  const save = async () => {
    try {
      const values = await form.validateFields();
      const effectiveFrom = values.effectiveFrom.format(DATE);
      run.mutate(async () => {
        if (mode === 'add') {
          await api.saveTeam({ ...teamPayload(team!), memberIds: values.userIds, membersFrom: effectiveFrom });
          feedback.success('Membri adaugati.');
        } else if (mode === 'swap') {
          feedback.success(
            transferMessage(
              (await api.swap({ userId: values.userId, otherUserId: values.otherUserId, effectiveFrom }))
                .affectedAllocations,
            ),
          );
        } else {
          feedback.success(
            transferMessage(
              (await api.move({ userId: values.userId, teamId: mode === 'end' ? null : values.teamId, effectiveFrom }))
                .affectedAllocations,
            ),
          );
        }
        onClose();
      });
    } catch (error) {
      feedback.error(error);
    }
  };
  return (
    <Modal
      open
      title={titles[mode]}
      onCancel={onClose}
      onOk={save}
      confirmLoading={run.isPending}
      okText='Salveaza'
      cancelText='Renunta'
      destroyOnHidden
    >
      {mode !== 'add' && (
        <Alert
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
          message='Alocarile existente raman neschimbate. Pentru un imprumut temporar, alocati persoana direct in Planificare.'
        />
      )}
      <Form
        form={form}
        layout='vertical'
        initialValues={{
          userId: member?.userId,
          effectiveFrom: member && member.validFrom > today() ? dayjs(member.validFrom) : dayjs(),
        }}
      >
        {mode === 'add' ? (
          <Form.Item name='userIds' label='Persoane' rules={[{ required: true, type: 'array', min: 1 }]}>
            <UserSelect multiple />
          </Form.Item>
        ) : member ? (
          <Form.Item name='userId' label='Persoana'>
            <Typography.Text strong>
              <UserOutlined /> {member.userName}
            </Typography.Text>
          </Form.Item>
        ) : (
          <Form.Item name='userId' label='Persoana' rules={[{ required: true }]}>
            <UserSelect />
          </Form.Item>
        )}
        {mode === 'move' && (
          <Form.Item name='teamId' label='Echipa destinatie' rules={[{ required: true }]}>
            <Select
              options={teams
                .filter((item) => item.isActive && item.id !== team?.id)
                .map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
        )}
        {mode === 'swap' && (
          <Form.Item name='otherUserId' label='Schimb cu persoana' rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp='label'
              options={swapOptions.filter((option) => option.value !== member?.userId)}
            />
          </Form.Item>
        )}
        <Form.Item
          name='effectiveFrom'
          label='Incepand cu data'
          extra={mode === 'end' ? 'Persoana nu mai face parte din echipa incepand cu aceasta data.' : undefined}
          rules={[
            { required: true },
            {
              validator: (_, value) =>
                mode !== 'end' ||
                !member ||
                !value ||
                (value.format(DATE) >= member.validFrom && (!member.validTo || value.format(DATE) < member.validTo))
                  ? Promise.resolve()
                  : Promise.reject(new Error('Selectati o data din perioada de apartenenta la echipa.')),
            },
          ]}
        >
          <DatePicker format={DISPLAY_DATE} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const TeamsTab: React.FC = () => {
  const { data: teams = [], isLoading, error } = useTeams();
  // Team leads see the teams; only administrators, the director and planning managers change them.
  const me = useMe();
  const { canPlan } = me;
  // A team lead allocates only the members of the teams they lead (the server enforces the same rule).
  const canAllocate = (team: Team) =>
    team.isActive && (me.canPlan || (me.canAllocate && me.ledTeamIds.includes(team.id)));
  const [allocating, setAllocating] = useState<TeamAllocation | null>(null);
  // A scheduled member is planned from the day they join the team.
  const allocate = (team: Team, member?: Membership, date = today()) =>
    setAllocating({ team, member, from: member && member.validFrom > date ? member.validFrom : date });
  const feedback = useFeedback();
  const [editing, setEditing] = useState<Team | 'new' | null>(null);
  const [action, setAction] = useState<MemberAction | null>(null);
  const [period, setPeriod] = useState<{ team: Team; member?: Membership } | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [layout, setLayout] = useState<Layout>('board');
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  const setLeader = (team: Team, userId: string | null) =>
    run.mutate(async () => {
      await api.saveTeam({ ...teamPayload(team), leaderUserId: userId });
      feedback.success(userId ? 'Sef de echipa actualizat.' : 'Rolul de sef a fost retras.');
    });
  const rows = teams.filter(
    (team) =>
      (showInactive || team.isActive) &&
      `${team.name} ${team.leaderName || ''} ${team.memberships.map((member) => member.userName).join(' ')}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const stateIcon = {
    current: (
      <Tooltip title='Curent'>
        <CheckCircleFilled style={{ color: '#52c41a' }} />
      </Tooltip>
    ),
    scheduled: (
      <Tooltip title='Programat'>
        <ClockCircleOutlined style={{ color: '#1677ff' }} />
      </Tooltip>
    ),
    history: (
      <Tooltip title='Istoric'>
        <HistoryOutlined style={{ color: '#bfbfbf' }} />
      </Tooltip>
    ),
  };

  const modals = (
    <>
      {editing && <TeamModal team={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {action && <MemberModal action={action} teams={teams} onClose={() => setAction(null)} />}
      {period?.member && (
        <MembershipPeriodModal
          key={`${period.member.id}:${period.member.rowVersion}`}
          member={period.member}
          team={period.team}
          teams={teams}
          onClose={() => setPeriod(null)}
        />
      )}
      {period && !period.member && <TeamStartModal team={period.team} onClose={() => setPeriod(null)} />}
      {allocating && (
        <AllocationModal
          defaults={{ teamId: allocating.team.id, userId: allocating.member?.userId }}
          from={allocating.from}
          to={dayjs(allocating.from).add(6, 'day').format(DATE)}
          onClose={() => setAllocating(null)}
        />
      )}
    </>
  );
  const layoutSwitch = (
    <Segmented<Layout>
      value={layout}
      onChange={setLayout}
      options={[
        { value: 'board', label: 'Panou', icon: <AppstoreOutlined /> },
        { value: 'table', label: 'Tabel', icon: <TableOutlined /> },
      ]}
    />
  );

  if (layout === 'board')
    return (
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <Space wrap>
          {layoutSwitch}
          {canPlan && (
            <>
              <IconButton
                title='Muta / adauga o persoana'
                icon={<UserSwitchOutlined />}
                onClick={() => setAction({ mode: 'move' })}
              />
              <IconButton title='Echipa noua' type='primary' icon={<PlusOutlined />} onClick={() => setEditing('new')} />
            </>
          )}
        </Space>
        {error && <Typography.Text type='danger'>{(error as Error).message}</Typography.Text>}
        <TeamBoard
          teams={teams}
          readOnly={!canPlan}
          onAdd={(team) => setAction({ mode: 'add', team })}
          onEdit={setEditing}
          onPeriod={(team, member) => setPeriod({ team, member })}
          onAllocate={allocate}
          canAllocate={canAllocate}
        />
        {modals}
      </Space>
    );

  return (
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
      <Space wrap>
        {layoutSwitch}
        <Input.Search
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder='Echipa sau persoana'
          allowClear
          style={{ width: 240 }}
        />
        <IconButton
          title={showInactive ? 'Ascunde echipele inactive' : 'Afiseaza echipele inactive'}
          icon={showInactive ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          type={showInactive ? 'primary' : 'default'}
          onClick={() => setShowInactive(!showInactive)}
        />
        <IconButton
          title={showHistory ? 'Ascunde istoricul membrilor' : 'Afiseaza istoricul membrilor'}
          icon={<HistoryOutlined />}
          type={showHistory ? 'primary' : 'default'}
          onClick={() => setShowHistory(!showHistory)}
        />
        {canPlan && (
          <>
            <IconButton
              title='Muta / adauga o persoana'
              icon={<UserSwitchOutlined />}
              onClick={() => setAction({ mode: 'move' })}
            />
            <IconButton title='Echipa noua' type='primary' icon={<PlusOutlined />} onClick={() => setEditing('new')} />
          </>
        )}
      </Space>
      {error && <Typography.Text type='danger'>{(error as Error).message}</Typography.Text>}
      <Table<Team>
        rowKey='id'
        dataSource={rows}
        loading={isLoading}
        scroll={{ x: 900 }}
        columns={[
          {
            title: 'Echipa',
            dataIndex: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (_, team) => (
              <Typography.Text strong type={team.isActive ? undefined : 'secondary'}>
                {team.name}
              </Typography.Text>
            ),
          },
          {
            title: 'Sef echipa',
            render: (_, team) =>
              team.leaderName ? (
                <Space size={4}>
                  <CrownFilled style={{ color: '#faad14' }} />
                  {team.leaderName}
                  {!isLeaderMember(team) && (
                    <Tooltip title='Seful nu mai este membru al echipei'>
                      <WarningFilled style={{ color: '#fa8c16' }} />
                    </Tooltip>
                  )}
                </Space>
              ) : (
                <Typography.Text type='secondary'>—</Typography.Text>
              ),
          },
          {
            title: 'Program',
            width: 110,
            sorter: (a, b) => a.dailyMinutes - b.dailyMinutes,
            render: (_, team) => (
              <Tooltip title='Program de lucru zilnic'>
                <Space size={4}>
                  <ClockCircleOutlined />
                  {hours(team.dailyMinutes)}
                </Space>
              </Tooltip>
            ),
          },
          {
            title: 'Membri curenti',
            render: (_, team) => {
              const current = team.memberships.filter((member) => memberState(member) === 'current');
              return (
                <Space size={8}>
                  <Avatar.Group max={{ count: 6 }} size='small'>
                    {current.map((member) => (
                      <Tooltip key={member.id} title={member.userName}>
                        <Avatar size='small' style={{ background: colorOf(member.userId) }}>
                          {initials(member.userName)}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </Avatar.Group>
                  <Typography.Text type='secondary'>{current.length}</Typography.Text>
                </Space>
              );
            },
          },
          {
            title: 'Stare',
            width: 70,
            align: 'center',
            render: (_, team) =>
              team.isActive ? (
                <Tooltip title='Activa'>
                  <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                </Tooltip>
              ) : (
                <Tooltip title='Inactiva'>
                  <StopOutlined style={{ color: '#bfbfbf', fontSize: 16 }} />
                </Tooltip>
              ),
          },
          {
            title: '',
            width: 280,
            fixed: 'right',
            render: (_, team) => (
              <Space size={4}>
                <PlanningLink teamId={team.id} name={team.name} />
                {canAllocate(team) && (
                  <IconButton
                    title='Aloca toata echipa pe un proiect'
                    size='small'
                    icon={<ScheduleOutlined />}
                    disabled={!activeMembers(team).length}
                    onClick={() => allocate(team)}
                  />
                )}
                {canPlan && (
                  <IconButton
                    title='Editeaza echipa'
                    size='small'
                    icon={<EditOutlined />}
                    onClick={() => setEditing(team)}
                  />
                )}
                {canPlan && team.isActive && (
                  <IconButton
                    title='Adauga membri'
                    size='small'
                    icon={<UserAddOutlined />}
                    onClick={() => setAction({ mode: 'add', team })}
                  />
                )}
                {canPlan && team.isActive && (
                  <IconButton
                    title='Data de inceput pentru toata echipa'
                    size='small'
                    icon={<FieldTimeOutlined />}
                    onClick={() => setPeriod({ team })}
                  />
                )}
              </Space>
            ),
          },
        ]}
        expandable={{
          expandedRowRender: (team) => {
            const order = { current: 0, scheduled: 1, history: 2 };
            const members = team.memberships
              .filter((member) => showHistory || memberState(member) !== 'history')
              .sort(
                (a, b) =>
                  order[memberState(a)] - order[memberState(b)] ||
                  Number(b.userId === team.leaderUserId) - Number(a.userId === team.leaderUserId) ||
                  a.userName.localeCompare(b.userName),
              );
            return (
              <Table<Membership>
                rowKey='id'
                dataSource={members}
                size='small'
                pagination={false}
                locale={{ emptyText: 'Niciun membru' }}
                columns={[
                  {
                    title: 'Membru',
                    render: (_, member) => {
                      const leader = member.userId === team.leaderUserId && memberState(member) !== 'history';
                      return (
                        <Space size={6}>
                          <Tooltip title={leader ? 'Sef echipa' : 'Membru'}>
                            {leader ? (
                              <CrownFilled style={{ color: '#faad14' }} />
                            ) : (
                              <UserOutlined style={{ color: '#8c8c8c' }} />
                            )}
                          </Tooltip>
                          {member.userName}
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Perioada',
                    render: (_, member) => membershipPeriod(member),
                  },
                  { title: 'Stare', width: 70, align: 'center', render: (_, member) => stateIcon[memberState(member)] },
                  {
                    title: '',
                    width: 220,
                    render: (_, member) => {
                      if (!team.isActive) return null;
                      const allocateButton = memberState(member) !== 'history' && canAllocate(team) && (
                        <IconButton
                          title='Aloca pe un proiect'
                          size='small'
                          icon={<ScheduleOutlined />}
                          onClick={() => allocate(team, member)}
                        />
                      );
                      if (!canPlan) return allocateButton || null;
                      const periodButton = (
                        <IconButton
                          title='Modifica perioada'
                          size='small'
                          icon={<FieldTimeOutlined />}
                          onClick={() => setPeriod({ team, member })}
                        />
                      );
                      if (memberState(member) === 'history') return periodButton;
                      const leader = member.userId === team.leaderUserId;
                      return (
                        <Space size={4}>
                          {allocateButton}
                          {periodButton}
                          <IconButton
                            title={leader ? 'Retrage rolul de sef' : 'Seteaza ca sef echipa'}
                            size='small'
                            loading={run.isPending}
                            icon={leader ? <CrownFilled style={{ color: '#faad14' }} /> : <CrownOutlined />}
                            onClick={() => setLeader(team, leader ? null : member.userId)}
                          />
                          <IconButton
                            title='Muta in alta echipa'
                            size='small'
                            icon={<ExportOutlined />}
                            onClick={() => setAction({ mode: 'move', team, member })}
                          />
                          <IconButton
                            title='Schimba cu o persoana din alta echipa'
                            size='small'
                            icon={<SwapOutlined />}
                            onClick={() => setAction({ mode: 'swap', team, member })}
                          />
                          <IconButton
                            title='Scoate din echipa'
                            size='small'
                            danger
                            icon={<UserDeleteOutlined />}
                            onClick={() => setAction({ mode: 'end', team, member })}
                          />
                        </Space>
                      );
                    },
                  },
                ]}
              />
            );
          },
        }}
      />
      {modals}
    </Space>
  );
};
export default TeamsTab;
